import urljoin from 'url-join';

export const ENV = process.env.ENV
export const AGENT_WIDGET_URL = "http://localhost:5173"
export const SUPPORT_AGENT_API = "https://localhost:3010"
export const API_HGT_BASEPATH = process.env.API_HGT_BASEPATH
export const API_AUDITTRAIL_BASEPATH = process.env.API_AUDITTRAIL_BASEPATH
export const DATADOG_TOKEN = process.env.DATADOG_TOKEN
export const AUTH_DOMAIN = process.env.AUTH_DOMAIN
export const AUTH_CLIENTID = process.env.AUTH_CLIENTID
export const IDLETIMER_IDLESTATUS_AWAY = process.env.IDLETIMER_IDLESTATUS_AWAY
export const IDLETIMER_IDLESTATUS_INACTIVE = process.env.IDLETIMER_IDLESTATUS_INACTIVE
export const IDLETIMER_IDLESTATUS_EXPIRED = process.env.IDLETIMER_IDLESTATUS_EXPIRED
export const IDLETIMER_COUNTDOWN = process.env.IDLETIMER_COUNTDOWN
export const URL_SURVEYOR_SSO = process.env.URL_SURVEYOR_SSO
export const API_BIOVIZ = process.env.API_BIOVIZ
export const API_KITS_UI = process.env.API_KITS_UI

/**
 * GlobalViz usage-insights ingest URL (Usage Insights MVP, workstream B).
 *
 * Aperture POSTs `portal_results_view` and `portal_results_download` cross-origin
 * with `Authorization: Bearer` and the same Auth0 access token `setBinfAuthCookie`
 * stores and `biovizApi` already sends (audience
 * `https://portal-api`). No cookies are sent.
 *
 * Login and logout are not emitted (locked Option B). First IBP engagement is
 * inferred from the first GlobalViz session / `module_enter`. PublishDate is
 * not included in the body; the warehouse joins it from existing PortalApi
 * project reads.
 *
 * Set per environment in `.env.<environment>` (CI copies it to `.env` before
 * `yarn build`). Unset means the emitters no-op. The exact
 * /usage-insights/ingest URL (no trailing slash, query, or fragment) must share the
 * `API_BIOVIZ` origin and use https (http only on loopback); anything else is
 * ignored, because the request carries the PortalApi bearer token.
 *
 * Expected request once the URL is live:
 *   POST $GLOBALVIZ_USAGE_INGEST_URL
 *   Authorization: Bearer <Auth0 access token>
 *   Content-Type: application/json
 *   credentials: omit, redirect: error
 *   {
 *     event_name: "portal_results_view" | "portal_results_download",
 *     event_id: "<uuid v4, new per event>",
 *     user_id: "<opaque /users/current userId>",
 *     account_id: "<client GUID or null>",
 *     project_id: "<ProjectId GUID or null>",
 *     session_id: "<uuid>", browser_tab_id: "<uuid>",
 *     module_id: null,
 *     app: "aperture",
 *     client_ts: "<ISO-8601>",
 *     schema_version: 1
 *   }
 * GlobalViz must allow CORS POST with `Authorization, Content-Type` listed
 * explicitly, answer the exact URL without a redirect, derive the user from
 * the token rather than `user_id`, and dedupe on that user plus `event_id`.
 * Aperture never retries, so a repeated `event_id` is a replay.
 */
export const GLOBALVIZ_USAGE_INGEST_URL = process.env.GLOBALVIZ_USAGE_INGEST_URL;
export const API_LIBRARY_EXPLORER_UI = process.env.API_LIBRARY_EXPLORER_UI
export const API_BASEPATH_PORTAL = process.env.API_BASEPATH_PORTAL
export const API_BASEPATH_PATHWAY = process.env.API_BASEPATH_PATHWAY
export const API_BASEPATH_DISCOVERY = process.env.API_BASEPATH_DISCOVERY
export const ENABLE_KITS_UI = process.env.ENABLE_KITS_UI === 'true';
export const ENABLE_LIBRARY_EXPLORER_UI = process.env.ENABLE_LIBRARY_EXPLORER_UI === 'true';
export const ENABLE_LEARNING_CENTER_CARD = process.env.ENABLE_LEARNING_CENTER_CARD === 'true';
export const ENABLE_ASSISTANT_WIDGET = process.env.ENABLE_ASSISTANT_WIDGET === 'true';
export const ENABLE_AGENT_CONTROLS = process.env.ENABLE_AGENT_CONTROLS === 'true';
export const ENABLE_EULA_MODAL = process.env.ENABLE_EULA_MODAL !== 'false'; // Default to true, set to 'false' to disable
export const ENABLE_PIPELINE_STATUS = process.env.ENABLE_PIPELINE_STATUS === 'true';
export const ENABLE_LAB_VANTAGE_IMPORT = process.env.ENABLE_LAB_VANTAGE_IMPORT === 'true';

/**
 * User-menu Pipeline Status mock showcase at `/home/pipeline-tracker`. Local/dev
 * only. Independent of `ENABLE_PIPELINE_STATUS` and `USE_PIPELINE_STATUS_API_MOCKS`.
 */
export const ENABLE_PIPELINE_TRACKER = process.env.ENABLE_PIPELINE_TRACKER === 'true';

/**
 * When true with `ENABLE_PIPELINE_STATUS`, Portal RTK pipeline-status GETs return
 * in-repo fixtures (Jest / rare local override) and SignalR is skipped.
 * Keep false in alpha/stage/prod and in local `.env` so project Home uses PortalApi.
 * The User-menu mock showcase is `ENABLE_PIPELINE_TRACKER`, not this flag.
 */
export const USE_PIPELINE_STATUS_API_MOCKS =
	ENABLE_PIPELINE_STATUS &&
	String(process.env.USE_PIPELINE_STATUS_API_MOCKS ?? '').trim().toLowerCase() === 'true';

/**
 * Non-empty when `PIPELINE_STATUS_MOCK_SCENARIO` env selects a fixture flow key (`singleV7ProjectFlow` or `singleCoreProjectFlow`).
 * When unset, mock scenario follows `ProjectDescription.pipeline` (see `requirePipelineStatusMockScenarioFromEnv`).
 * The facts request forwards that pipeline as `params.pipeline` so the mock shim does not
 * depend on the RTK `projectId` cache (the project page loads via classic Redux).
 */
export const PIPELINE_STATUS_MOCK_SCENARIO = (() => {
	const raw = process.env.PIPELINE_STATUS_MOCK_SCENARIO;
	return raw != null && String(raw).trim() !== '' ? String(raw).trim() : undefined;
})();

/**
 * Polling fallback interval (ms) for the project pipeline-status query. SignalR remains
 * the primary invalidation hint; polling recovers missed invalidations and disconnects.
 * Configurable via `PIPELINE_STATUS_POLL_INTERVAL_MS`; `0` disables polling; default 60s.
 */
export const PIPELINE_STATUS_POLL_INTERVAL_MS = (() => {
	const raw = String(process.env.PIPELINE_STATUS_POLL_INTERVAL_MS ?? '').trim();
	if (raw === '') return 60000;
	const n = Number(raw);
	return Number.isFinite(n) && n >= 0 ? n : 60000;
})();
export const ENABLE_SECURITY_ALERT = process.env.ENABLE_SECURITY_ALERT === 'true'; // Default to false, set to 'true' to enable
export const AUTH_CALLBACKURL = `${window.origin}/callback`;

/**Hub Hosts */
export const HUBS_BASEPATH = API_BASEPATH_PORTAL.replace("api/v1", "hubs");

/** Audit Trail */
export const API_AUDITTRAIL_USER_ACTION = urljoin(`${API_AUDITTRAIL_BASEPATH}`, 'user/action');
export const API_AUDITTRAIL_USER = urljoin(`${API_AUDITTRAIL_BASEPATH}`, 'user');
export const API_AUDITTRAIL_PROJECT_VIEW = urljoin(`${API_AUDITTRAIL_BASEPATH}`, 'project/view');
export const API_AUDITTRAIL_PROJECT_DOWNLOAD = urljoin(`${API_AUDITTRAIL_BASEPATH}`, 'project/download');
export const API_AUDITTRAIL_PROJECT_PERMISSIONS = urljoin(`${API_AUDITTRAIL_BASEPATH}`, 'user/permission/project');

/**Console Log */
export const CONSOLELOG_REDUXACTIONS = false;
export const CONSOLELOG_IDLEACTIONS = false;

/** Authentication Configuration */
export const AUTH_CONTAINER = "auth0-login-container";
export const AUTH_DBCONNECTIONNAME = "Username-Password-Authentication";

/**Data Dog */
export const DATADOG_URL = "datadoghq.com";

/**Endpoints  */
export const API_ENDPOINT_GLOBAL_SEARCH = urljoin(API_BASEPATH_PORTAL,'search/simple/');
export const API_ENDPOINT_FILES = urljoin(API_BASEPATH_PORTAL,'files/project/');
export const API_ENDPOINT_FILES_DOWNLOAD = urljoin(API_BASEPATH_PORTAL,'files/download');
export const API_ENDPOINT_FILES_DOWNLOAD_ALL_BY_PROJECT = API_ENDPOINT_FILES_DOWNLOAD;
export const API_ENDPOINT_MANAGEDUSERS = urljoin(API_BASEPATH_PORTAL,'users');

export const API_ENDPOINT_MANAGEDUSERS_SEARCH = urljoin(API_BASEPATH_PORTAL,'users/email/contains');
export const API_ENDPOINT_MANAGEDPROJECT = urljoin(API_BASEPATH_PORTAL,'admin/users/projects');
export const API_ENDPOINT_MANAGEDPROJECT_SEARCH = urljoin(API_BASEPATH_PORTAL,'search/simple/');
export const API_ENDPOINT_USERLOGIN = urljoin(API_BASEPATH_PORTAL,'users/current');
export const API_ENDPOINT_PROJECT = urljoin(API_BASEPATH_PORTAL,'projects');
export const API_ENDPOINT_PROJECT_BYPROJECTCODE = urljoin(API_BASEPATH_PORTAL,'projects/projectCode');

//export const API_BIOVIZ = "https://globalviz-dev.metabolon.com";
export const API_BIOVIZ_STATS = urljoin(`${API_BIOVIZ}`, 'stats');

// agent widget
export const API_ENDPOINT_AGENT_TOKEN = urljoin(API_BASEPATH_PORTAL, 'assistant/token/portal');

// discovery panels
export const API_ENDPOINT_SMART_PANELS = urljoin(API_BASEPATH_DISCOVERY, 'client', 'projects');
export const API_ENDPOINT_SMART_PANELS_ADMIN_PROJECTIDS = urljoin(API_BASEPATH_DISCOVERY, 'admin','projectids');
export const API_ENDPOINT_SMART_PANELS_ADMIN_SAMPLESETIDS = urljoin(API_BASEPATH_DISCOVERY, 'admin','samplesetids');
export const API_ENDPOINT_SMART_PANELS_SAMPLESETIDS = urljoin(API_BASEPATH_DISCOVERY, 'client','samplesetids');
export const API_ENDPOINT_SMART_PANELS_PROJECTIDS = urljoin(API_BASEPATH_DISCOVERY, 'client','projectids');
export const API_ENDPOINT_SMART_PANELS_CDT = urljoin(API_BASEPATH_DISCOVERY,'client','CDT','download');
export const API_ENDPOINT_SMART_PANELS_FILE = urljoin(API_BASEPATH_DISCOVERY,'client', 'files', 'download');
export const API_ENDPOINT_SMART_PANELS_HEATMAP = urljoin(API_BASEPATH_DISCOVERY,'client', 'heatmap', 'download');
export const API_ENDPOINT_SMART_PANELS_RESULTS = urljoin(API_BASEPATH_DISCOVERY,'client', 'Visualization', 'download');
export const API_ENDPOINT_SMART_PANELS_SAMPLESET = urljoin(API_BASEPATH_DISCOVERY,'client', 'SampleSet', 'download');

export const API_ENDPOINT_SAMPLESETDATA = urljoin(API_BASEPATH_PATHWAY,'samplesets');

/** Pathway Map endpoints */
export const API_ENDPOINT_PATHWAY_PLANT = urljoin(API_BASEPATH_PATHWAY,'pathwaymaps/PLANT');
export const API_ENDPOINT_PATHWAY_ANIMAL = urljoin(API_BASEPATH_PATHWAY,'pathwaymaps/ANIMAL');

export const API_ENDPOINT_PATHWAY_PLANT_MAP = urljoin(API_BASEPATH_PATHWAY,'pathwaymaps/file/get/map-plant-v5.json');
export const API_ENDPOINT_PATHWAY_ANIMAL_MAP = urljoin(API_BASEPATH_PATHWAY,'pathwaymaps/file/get/map-animal-v5.json');

export const API_ENDPOINT_PATHWAY_PLANT_PUT_MAP = urljoin(API_BASEPATH_PATHWAY,'pathwaymaps/file/post');
export const API_ENDPOINT_PATHWAY_ANIMAL_PUT_MAP = urljoin(API_BASEPATH_PATHWAY,'pathwaymaps/file/post');

/**Admin Endpoints */
export const API_ENDPOINT_FILES_ADMIN = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'project');
export const API_ENDPOINT_FILES_ADMIN_DOWNLOAD = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'download');
export const API_ENDPOINT_PUBLISH_FILES = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'publish', 'collection');
export const API_ENDPOINT_PUBLISH_FILE = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'publish', 'single');
export const API_ENDPOINT_REMOVE_FILE = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'remove', 'single');
export const API_ENDPOINT_UPDATE_FILE_PROPERTIES = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'update', 'single');

export const API_ENDPOINT_SAMPLESETDATA_ADMIN = urljoin(API_BASEPATH_PORTAL,'admin/samplesets/project/');
export const API_ENDPOINT_SAMPLESETDATA_ADMIN_SAVE = urljoin(API_BASEPATH_PORTAL,'admin/samplesets/save/single');
export const API_ENDPOINT_SAMPLESETDATA_ADMIN_SAVE_ALL = urljoin(API_BASEPATH_PORTAL,'admin/samplesets/save/collection');
export const API_ENDPOINT_SAMPLESETDATA_ADMIN_PUBLISH_PROJECT = urljoin(API_BASEPATH_PORTAL,'admin/samplesets/publish/project');

export const API_ENDPOINT_PROJECT_ADMIN = urljoin(API_BASEPATH_PORTAL,'admin/projects/');

export const API_ENDPOINT_PROJECT_ADMIN_SAVE = urljoin(API_BASEPATH_PORTAL,'admin/projects/save');
export const API_ENDPOINT_PROJECT_ADMIN_PUBLISH = urljoin(API_BASEPATH_PORTAL,'admin/projects/publish/project');
export const API_ENDPOINT_PROJECT_PUBLISH_SUMMARY = urljoin(API_BASEPATH_PORTAL,'admin/projects/summary');
export const API_ENDPOINT_PROJECT_ADMIN_CREATE = urljoin(API_BASEPATH_PORTAL,'admin/projects/publish/convert');

export const API_ENDPOINT_PROJECT_USERS = urljoin(API_BASEPATH_PORTAL,'admin', 'users', 'project');

export const API_ENDPOINT_USERS = urljoin(API_BASEPATH_PORTAL,'users');
export const API_ENDPOINT_USERS_ADMIN = urljoin(API_BASEPATH_PORTAL,'admin/users');
export const API_ENDPOINT_USERS_UPDATE = urljoin(API_BASEPATH_PORTAL,'admin/users/update');
export const API_ENDPOINT_USERS_ADMIN_UPDATE = urljoin(API_BASEPATH_PORTAL,'admin/users/publish');
// Still valid - matches GET /api/v1/admin/users/roles
export const API_ENDPOINT_USERS_AVAILABLEROLES = urljoin(API_BASEPATH_PORTAL,'admin/users/roles');
export const API_ENDPOINT_USERS_EMAIL = urljoin(API_BASEPATH_PORTAL,'admin/users');
export const API_ENDPOINT_USERS_DETAILS= urljoin(API_BASEPATH_PORTAL,'users/details/contains');
export const API_ENDPOINT_USERS_USERIDS = urljoin(API_BASEPATH_PORTAL,'admin/users/collection');
export const API_ENDPOINT_PUBLISHING_STATUS = urljoin(API_BASEPATH_PORTAL,'admin/projects/publish/status');
export const API_ENDPOINT_PROJECTS_UPDATE_STATUS = urljoin(API_BASEPATH_PORTAL,'admin/projects/update/status/');
export const API_ENDPOINT_STAKEHOLDERS_ADMIN = urljoin(API_BASEPATH_PORTAL,'admin/projects/stakeholders');

/**File Uploader Endpoints */
export const API_ATTACHMENT_UPLOAD = urljoin(API_BASEPATH_PORTAL,'admin', 'files', 'upload', 'single');

/**DemoEmail */
export const demoEmailAddress = "portaldemo@metabolon.com";
export const supportEmailAddress = "portalsupport@metabolon.com";
export const demoSubject = "Request for demo";
export const demoBody = "(Please provide the information below so we connect you with the appropriate demo.)%0D%0A%0D%0AFirst Name:%0D%0ALast Name:%0D%0AEmail:%0D%0ACompany:%0D%0AIndustry:%0D%0AAddress:%0D%0AZip Code:%0D%0ACity:%0D%0AState or Territory:%0D%0ACounty Code (2 digit):";
export const getADemoLink = "https://www.metabolon.com/get-started/";
export const webpackversion = "4.0";

/** Revenue Recognition */
export const API_ENDPOINT_RECOGNIZE_REVENUE = urljoin(API_BASEPATH_PORTAL,'projects/recognize-revenue');
